import { uploadMessagesForAI } from '@api/chat';
import toast from '@components/Common/Toast.js';
import { CheckIcon } from '@icons/IconLibrary';
import { aiSelectionModeChanged } from '@signals/aiAnalyzer/index.js';
import { selectionModeChanged } from '@signals/chat';
import { COLUMN_EVENT_TYPES, columnUpdate } from '@signals/project/index.js';
import ProjectsState, { LoadedFolderIds } from '@states/Projects';
import UserState from '@states/User';
import {
  UIButton,
  UIDiv,
  UIImage,
  UILabel,
  UIParagraph,
  UISelect,
  UIText,
  UITextArea,
} from '@ui';

export const ACTION_LABELS = {
  'create-project': 'Create Project',
  'update-project': 'Update Project',
  'create-task': 'Create Task',
  'update-task': 'Update Task',
};

export default class UploadMessagesView extends UIDiv {
  /**
   * @param {string} action - The action to perform (create-task, update-task, etc.)
   * @param {Array|Map} messagesOrContent - For chat: Array of messages. For AI: Map of selected content
   * @param {Function} returnToThread - Callback to return to previous view
   * @param {Array} roomUsers - Room users (chat mode only)
   * @param {string|null} roomIdOrSessionId - For chat: roomId. For AI: sessionId
   * @param {string} mode - 'chat' or 'ai' to determine the source type
   */
  constructor(
    action,
    messagesOrContent,
    returnToThread,
    roomUsers = [],
    roomIdOrSessionId = null,
    mode = 'chat',
  ) {
    super();
    this.action = action;
    this.mode = mode; // 'chat' or 'ai'
    this.returnToThread = returnToThread;

    // Handle different data structures based on mode
    if (mode === 'ai') {
      this.selectedContent = messagesOrContent; // Map for AI
      this.sessionId = roomIdOrSessionId;
      this.messages = this.#convertContentToMessages(
        messagesOrContent,
        roomIdOrSessionId,
      );
      this.roomUsers = [];
      this.roomId = null;
    } else {
      this.messages = messagesOrContent; // Array for chat
      this.selectedContent = null;
      this.sessionId = null;
      this.roomUsers = roomUsers;
      this.roomId = roomIdOrSessionId;
    }

    this.treatAsSeparate = false;
    this.projectsLoaded = false;
    this.selectedProjectId = null;
    this.selectedTaskId = null;
    this.isLoading = false;
    this.isSuccess = false;
    this.actionButton = null;
    this.actionButtonContent = null;
    this.additionalNotes = '';

    this.setClass('h-full pt-2 w-full flex flex-col bg-white overflow-y-auto');

    this.#render();
  }

  // Convert AI selected content Map to messages array format
  #convertContentToMessages(selectedContent, sessionId) {
    if (!selectedContent || !(selectedContent instanceof Map)) {
      return [];
    }

    return Array.from(selectedContent.values()).map((item, index) => ({
      id: `ai-${sessionId}-${index}`,
      // remove the line breaks because AI will hallucinate and treat the content seperately
      content: item.content.replace(/\n+/g, ' ').trim(),
      senderName: 'AI Assistant',
      senderId: 'ai-system',
      timestamp: item.timestamp || Date.now(),
      files: [], // AI content doesn't have files
      elementType: item.elementType, // Store original element type
    }));
  }

  // we want to loadProjects if the state hasns't been populated yet
  async #loadProjects() {
    await ProjectsState.loadProjects(UserState.user?.selectedOrg);
  }

  #render() {
    this.clear();

    // Content Section
    const contentSection = new UIDiv().setClass('flex-1 px-4 pb-4 space-y-4');

    // Save Location Section (for updates)
    if (this.action.includes('update') || this.action.includes('task')) {
      const saveSectionContainer = new UIDiv().setClass('space-y-2');
      const saveSection = this.#createSaveSectionSkeleton();
      saveSectionContainer.add(saveSection);
      contentSection.add(saveSectionContainer);

      // Load projects if not already loaded (non-blocking)
      if (
        !ProjectsState.projects.hasBeenLoaded ||
        ProjectsState.projects.rootFolderOrganizationId !==
          UserState.user?.selectedOrg
      ) {
        this.#loadProjects().then(() => {
          this.projectsLoaded = true;
          // Replace skeleton with actual content
          const actualSaveSection =
            this.#createSaveSection(saveSectionContainer);
          saveSection.dom.replaceWith(actualSaveSection.dom);
        });
      } else {
        this.projectsLoaded = true;
        // Replace skeleton with actual content immediately
        const actualSaveSection = this.#createSaveSection(saveSectionContainer);
        saveSection.dom.replaceWith(actualSaveSection.dom);
      }
    }

    // Multiple Messages Toggle (if more than 1 message selected)
    // if (this.messages.length > 1) {
    //   const toggleSection = this.#createToggleSection();
    //   contentSection.add(toggleSection);
    // }

    // Files Preview Section (only for chat mode)
    if (this.mode === 'chat') {
      const filesSection = this.#createFilesSection();
      if (filesSection) {
        contentSection.add(filesSection);
      }
    }

    // Messages/Content Preview Section
    const messagesSection = this.#createMessagesSection();
    contentSection.add(messagesSection);

    // Additional Notes Section
    const notesSection = this.#createNotesSection();
    contentSection.add(notesSection);

    this.add(contentSection);

    // Bottom Action Button
    const actionButton = this.#createActionButton();
    this.add(actionButton);
  }

  #createSaveSectionSkeleton() {
    const section = new UIDiv().setClass('space-y-2');

    const label = new UILabel();
    label.setClass('text-lg font-semibold text-gray-800');
    label.setTextContent('Select Project');
    section.add(label);

    const selectSkeleton = new UIDiv();
    selectSkeleton.setClass('w-full h-12 bg-gray-200 rounded-lg animate-pulse');
    section.add(selectSkeleton);

    return section;
  }

  #createSaveSection() {
    const section = new UIDiv().setClass('space-y-2');

    const label = new UILabel();
    label.setClass('text-lg font-semibold text-gray-800');
    label.setTextContent('Select Project');

    section.add(label);

    // Dropdown for selecting project/folder
    const projectsSelect = new UISelect();
    projectsSelect.setClass(
      'w-full px-4 py-3 border border-gray-300 rounded-lg text-base',
    );

    const isUpdateTaskAction = this.action.includes('update-task');
    // Build options based on loaded projects
    const options = [];

    options.push({ value: '', label: '— Choose a Project —' }); // Placeholder

    ProjectsState.projects.folders?.forEach(folder => {
      options.push({
        value: folder.id,
        label: `— ${folder.name}`,
      });
    });

    projectsSelect.setOptions(options, '', 'label', 'value');

    // Container for task selector (will be added dynamically)
    const taskSelectorContainer = new UIDiv();
    taskSelectorContainer.setClass('space-y-2');

    projectsSelect.onChange(async e => {
      this.selectedProjectId = e.target.value ? Number(e.target.value) : null;

      // Update task selector based on selected project
      if (isUpdateTaskAction) {
        taskSelectorContainer.clear();

        if (this.selectedProjectId) {
          // Show loading skeleton while fetching tasks
          const skeleton = new UIDiv().setClass(
            'w-full h-12 bg-gray-200 rounded-lg animate-pulse',
          );
          taskSelectorContainer.add(skeleton);

          // Ensure tasks for this project are fetched before rendering
          await ProjectsState.getProjectById(this.selectedProjectId);

          taskSelectorContainer.clear();
        }

        const taskSelector = this.#createTaskSelector(this.selectedProjectId);
        if (taskSelector) taskSelectorContainer.add(taskSelector);
      }
    });

    section.add(projectsSelect);

    // Add task selector for task actions
    if (isUpdateTaskAction) {
      section.add(taskSelectorContainer);
      // Always show task selector, will show placeholder if no project selected
      const initialTaskSelector = this.#createTaskSelector(
        this.selectedProjectId,
      );
      taskSelectorContainer.add(initialTaskSelector);
    }

    return section;
  }

  #createTaskSelector(projectId) {
    const container = new UIDiv().setClass('space-y-2');

    const label = new UILabel();
    label.setClass('text-base font-semibold text-gray-700');
    label.setTextContent('Update Task (Optional)');
    container.add(label);

    const taskSelect = new UISelect();
    taskSelect.setClass(
      'w-full px-4 py-3 border border-gray-300 rounded-lg text-base',
    );

    let taskOptions = [];

    if (!projectId) {
      // No project selected, show placeholder
      taskOptions = [{ value: '', label: '— Choose a Project First —' }];
      taskSelect.dom.disabled = true;
    } else {
      const tasks =
        ProjectsState.projects.tasks?.filter(
          task => task.parentId === projectId,
        ) || [];

      if (tasks.length === 0) {
        // Project selected but no tasks
        taskOptions = [{ value: '', label: 'No existing tasks.' }];
      } else {
        // Project selected with tasks
        taskOptions = [
          { value: '', label: '- Choose a Task -' },
          ...tasks.map(task => ({
            value: task.id,
            label: task.name,
          })),
        ];
      }
      taskSelect.dom.disabled = false;
    }

    taskSelect.setOptions(taskOptions, '', 'label', 'value');

    taskSelect.onChange(e => {
      this.selectedTaskId = e.target.value ? Number(e.target.value) : null;
    });

    container.add(taskSelect);

    return container;
  }

  // #createToggleSection() {
  //   const toggle = new Toggle({
  //     label: 'Treat messages separately',
  //     initialState: false,
  //     onChange: state => {
  //       this.treatAsSeparate = state;
  //     },
  //   });

  //   return toggle;
  // }

  #createMessagesSection() {
    const section = new UIDiv().setClass('space-y-2');

    const label = new UILabel();
    label.setClass('text-lg font-semibold text-gray-800');

    // Different labels for chat vs AI mode
    if (this.mode === 'ai') {
      const count = this.messages.length;
      label.setTextContent(
        `Selected Content (${count} item${count !== 1 ? 's' : ''})`,
      );
    } else {
      label.setTextContent(
        `Selected Message${this.messages.length > 1 ? 's' : ''} (${this.messages.length})`,
      );
    }

    section.add(label);

    // Messages container with max height and scroll
    const maxHeight = this.mode === 'ai' ? 'max-h-96' : 'max-h-64';
    const messagesContainer = new UIDiv().setClass(
      `space-y-2 ${maxHeight} overflow-y-auto border border-gray-200 rounded-lg p-3`,
    );

    this.messages.forEach((message, index) => {
      const messagePreview = this.#createMessagePreview(message, index);
      messagesContainer.add(messagePreview);
    });

    section.add(messagesContainer);

    return section;
  }

  #createMessagePreview(message, index) {
    const preview = new UIDiv().setClass(
      'p-3 bg-gray-50 rounded-lg border border-gray-200',
    );

    // Message header with sender and timestamp (or type badge for AI)
    const header = new UIDiv().setClass('flex items-center gap-2 mb-2');

    if (this.mode === 'ai' && message.elementType) {
      // Show element type badge for AI content
      const typeBadge = new UIDiv().setClass(
        'px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-semibold uppercase',
      );
      typeBadge.setTextContent(message.elementType);
      header.add(typeBadge);
    } else {
      // Show sender name for chat messages
      const senderName = new UIText(message.senderName || 'Unknown');
      senderName.setClass('text-sm font-semibold text-gray-800');
      header.add(senderName);
    }

    // Add timestamp only for chat mode
    if (this.mode === 'chat') {
      const timestamp = new UIText(
        new Date(message.timestamp).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
      );
      timestamp.setClass('text-xs text-gray-500 ml-auto');
      header.add(timestamp);
    }

    preview.add(header);

    // Message content (truncated)
    if (message.content) {
      const content = new UIParagraph();
      const lineClamp = this.mode === 'ai' ? 'line-clamp-3' : 'line-clamp-2';
      const whitespace = this.mode === 'ai' ? 'whitespace-pre-wrap' : '';
      content.setClass(`text-sm text-gray-700 ${lineClamp} ${whitespace}`);
      content.setTextContent(message.content);
      preview.add(content);
    }

    // File indicator if message has files (chat mode only)
    if (this.mode === 'chat' && message.files && message.files.length > 0) {
      const fileIndicator = new UIDiv().setClass(
        'mt-2 flex items-center gap-1 text-xs text-blue-600',
      );
      const fileIcon = new UIText(`📎 ${message.files.length} file(s)`);
      fileIndicator.add(fileIcon);
      preview.add(fileIndicator);
    }

    return preview;
  }

  #createFilesSection() {
    // Collect all files from all messages
    const allFiles = [];
    this.messages.forEach(message => {
      if (message.files && message.files.length > 0) {
        allFiles.push(...message.files);
      }
    });

    if (allFiles.length === 0) return null;

    const section = new UIDiv().setClass('space-y-2');

    const label = new UILabel();
    label.setClass('text-lg font-semibold text-gray-800');
    label.setTextContent(`Attached Files (${allFiles.length})`);

    section.add(label);

    // Files preview container (horizontal scroll)
    const filesContainer = new UIDiv().setClass(
      'flex gap-2 overflow-x-auto pb-2',
    );
    filesContainer.setStyle('scrollbar-width', ['thin']);

    allFiles.forEach((file, index) => {
      const filePreview = this.#createFilePreview(file, index);
      filesContainer.add(filePreview);
    });

    section.add(filesContainer);

    return section;
  }

  #createFilePreview(file, index) {
    const fileInfo = this.#getFileInfo(file);

    const preview = new UIDiv().setClass(
      'flex-shrink-0 bg-gray-100 rounded-lg border border-gray-200 relative overflow-hidden',
    );

    // Determine if file can be displayed directly
    if (fileInfo.canDisplayDirectly) {
      if (fileInfo.fileType === 'IMAGE' && file.url) {
        preview.setClass(
          'flex-shrink-0 bg-gray-100 rounded-lg border border-gray-200 relative overflow-hidden',
        );

        const img = new UIImage(file.url);
        img.setClass('w-full h-full object-cover');
        img.setStyle('max-width', ['120px']);
        img.setStyle('max-height', ['120px']);
        img.setStyle('min-width', ['80px']);
        img.setStyle('min-height', ['80px']);
        preview.add(img);

        // Add extension overlay
        const overlayContainer = new UIDiv().setClass(
          'absolute top-1 left-1 pointer-events-none',
        );
        const extBadge = new UIDiv().setClass(
          'bg-black bg-opacity-70 text-white text-xs px-1 py-0.5 rounded font-mono',
        );
        extBadge.add(new UIText(fileInfo.extension));
        overlayContainer.add(extBadge);
        preview.add(overlayContainer);
      } else if (fileInfo.fileType === 'VIDEO' && file.url) {
        preview.setClass(
          'flex-shrink-0 bg-gray-100 rounded-lg border border-gray-200 relative overflow-hidden',
        );

        const videoElement = document.createElement('video');
        videoElement.src = file.url;
        videoElement.className = 'w-full h-full object-cover';
        videoElement.style.maxWidth = '120px';
        videoElement.style.maxHeight = '120px';
        videoElement.style.minWidth = '80px';
        videoElement.style.minHeight = '80px';

        const videoWrapper = new UIDiv();
        videoWrapper.dom.appendChild(videoElement);
        preview.add(videoWrapper);

        // Add extension overlay
        const overlayContainer = new UIDiv().setClass(
          'absolute top-1 left-1 pointer-events-none',
        );
        const extBadge = new UIDiv().setClass(
          'bg-black bg-opacity-70 text-white text-xs px-1 py-0.5 rounded font-mono',
        );
        extBadge.add(new UIText(fileInfo.extension));
        overlayContainer.add(extBadge);
        preview.add(overlayContainer);
      }
    } else {
      // Show file info for non-displayable files
      preview.setClass(
        'flex-shrink-0 w-32 h-32 bg-gray-50 rounded-lg border border-gray-200 relative overflow-hidden',
      );

      const infoContainer = new UIDiv().setClass(
        'w-full h-full flex flex-col items-center justify-center gap-2 p-2',
      );

      const fileIconMap = {
        PDF: '📄',
        ARCHIVE: '📦',
        '3D MODEL': '🧊',
        FILE: '📎',
      };

      const fileIcon = new UIParagraph();
      fileIcon.setClass('text-3xl');
      fileIcon.setTextContent(fileIconMap[fileInfo.fileType] || '📎');
      infoContainer.add(fileIcon);

      // Extension badge
      const extBadge = new UIDiv().setClass(
        'px-2 py-0.5 bg-white border border-gray-300 rounded text-xs font-mono text-gray-700',
      );
      extBadge.add(new UIText(fileInfo.extension));
      infoContainer.add(extBadge);

      // File type label
      const typeLabel = new UIText(fileInfo.fileType).setClass(
        'text-xs text-gray-600 font-medium',
      );
      infoContainer.add(typeLabel);

      preview.add(infoContainer);
    }

    // File name tooltip
    preview.setTitle(file.filename || `File ${index + 1}`);

    return preview;
  }

  #getFileInfo(file) {
    const filename = file.filename || file.body || 'Unknown file';
    const extension = filename.split('.').pop()?.toLowerCase() || '';
    const mimeType = file.info?.mimetype || '';

    // Determine file type category
    let fileType = 'FILE';
    let canDisplayDirectly = false;

    if (
      mimeType.startsWith('image/') ||
      file.msgtype === 'm.image' ||
      ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(extension)
    ) {
      fileType = 'IMAGE';
      canDisplayDirectly = true;
    } else if (
      mimeType.startsWith('video/') ||
      file.msgtype === 'm.video' ||
      ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension)
    ) {
      fileType = 'VIDEO';
      canDisplayDirectly = true;
    } else if (['pdf'].includes(extension)) {
      fileType = 'PDF';
      canDisplayDirectly = false;
    } else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) {
      fileType = 'ARCHIVE';
      canDisplayDirectly = false;
    } else if (
      ['glb', 'gltf', 'obj', 'fbx', 'dae', 'ply', 'stl'].includes(extension)
    ) {
      fileType = '3D MODEL';
      canDisplayDirectly = false;
    }

    return {
      filename,
      extension: extension.toUpperCase(),
      fileType,
      canDisplayDirectly,
    };
  }

  #createNotesSection() {
    const section = new UIDiv().setClass('space-y-2');

    const label = new UILabel();
    label.setClass('text-lg font-semibold text-gray-800');
    label.setTextContent('Additional Notes (Optional)');
    section.add(label);

    const textarea = new UITextArea();
    textarea.setClass(
      'w-full px-4 py-3 border border-gray-300 rounded-lg text-base resize-none focus:outline-none focus:ring-2 focus:ring-blue-500',
    );
    textarea.setPlaceholder('Add any additional notes or instructions...');
    textarea.setRows(4);
    textarea.onInput(e => {
      this.additionalNotes = e.target.value;
    });

    const textareaWrapper = new UIDiv();
    textareaWrapper.add(textarea);
    section.add(textareaWrapper);

    return section;
  }

  #createActionButton() {
    const container = new UIDiv().setClass(
      'sticky bottom-0 bg-white border-t p-4 flex justify-center gap-4',
    );

    if (this.mode === 'ai') {
      const backButton = new UIButton(i18next.t('common.cancel'));
      backButton.setClass('px-4 py-2 min-w-[80px] cursor-pointer');
      backButton.onClick(() => {
        // Exit selection mode and return to previous view
        aiSelectionModeChanged.dispatch(false);
        if (this.returnToThread) {
          this.returnToThread();
        }
      });
      container.add(backButton);
    }

    // Get button text based on action
    let buttonText = 'Confirm';
    if (this.action.includes('create')) {
      buttonText = 'Create';
    } else if (this.action.includes('update')) {
      buttonText = 'Update';
    }

    this.actionButton = new UIButton();
    this.actionButton.setClass(
      'relative overflow-hidden bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-full text-lg w-full max-w-md transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed',
    );

    // Create content container
    this.actionButtonContent = new UIDiv();
    this.actionButtonContent.setClass(
      'flex items-center justify-center gap-2 transition-all duration-300',
    );

    const buttonTextElement = new UIText(buttonText);
    this.actionButtonContent.add(buttonTextElement);
    this.actionButton.add(this.actionButtonContent);

    this.actionButton.onClick(async () => {
      await this.#handleConfirm();
    });

    container.add(this.actionButton);

    return container;
  }

  #updateButtonState() {
    if (!this.actionButton || !this.actionButtonContent) return;

    // Clear previous content
    this.actionButtonContent.clear();

    if (this.isSuccess) {
      // Success state - green background with check icon
      this.actionButton.setClass(
        'relative overflow-hidden bg-green-500 hover:bg-green-500 text-white font-semibold px-8 py-3 rounded-full text-lg w-full max-w-md transition-all duration-300',
      );
      this.actionButton.dom.disabled = true;

      const checkIcon = new CheckIcon({
        color: 'none',
        strokeColor: 'white',
        size: 20,
      });
      checkIcon.setClass('animate-scale-in');
      this.actionButtonContent.add(checkIcon);

      const successText = new UIText(
        this.action.includes('create') ? 'Created!' : 'Updated!',
      );
      this.actionButtonContent.add(successText);
    } else if (this.isLoading) {
      // Loading state - add shimmer effect and animated dots
      this.actionButton.dom.disabled = true;

      // Add shimmer background
      const shimmer = new UIDiv();
      shimmer.setClass(
        'absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/20 to-transparent',
      );
      this.actionButton.dom.insertBefore(
        shimmer.dom,
        this.actionButtonContent.dom,
      );

      // Add animated dots
      const dotsContainer = new UIDiv();
      dotsContainer.setClass('flex gap-1');

      for (let i = 0; i < 3; i++) {
        const dot = new UIDiv();
        dot.setClass('w-2 h-2 rounded-full bg-white animate-bounce-dot');
        dot.setStyle('animation-delay', [`${i * 150}ms`]);
        dotsContainer.add(dot);
      }

      this.actionButtonContent.add(dotsContainer);

      const loadingText = new UIText('Processing');
      this.actionButtonContent.add(loadingText);
    } else {
      // Default state
      this.actionButton.setClass(
        'relative overflow-hidden bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-full text-lg w-full max-w-md transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed',
      );
      this.actionButton.dom.disabled = false;

      const buttonText = this.action.includes('create') ? 'Create' : 'Update';
      const buttonTextElement = new UIText(buttonText);
      this.actionButtonContent.add(buttonTextElement);
    }
  }

  async #handleConfirm() {
    // make a full checking based on the action type
    const { status, message } = this.#handleFormError();
    if (status === 'error') {
      toast.error(message);
      return;
    }

    // Set loading state
    this.isLoading = true;
    this.isSuccess = false;
    this.#updateButtonState();

    try {
      // Collect all data to send to API
      // Build payload based on mode
      const payload = {
        organizationId: UserState.user?.selectedOrg || null,
        action: this.action,
        treatAsSeparate: this.treatAsSeparate,
        selectedProjectId: this.selectedProjectId,
        selectedTaskId: this.selectedTaskId,
        additionalNotes: this.additionalNotes,
        roomUsers: this.roomUsers,
        messages: this.messages.map(msg => ({
          id: msg.id,
          content: msg.content,
          senderName: msg.senderName,
          senderId: msg.senderId,
          timestamp: msg.timestamp,
          mentions: msg.mentions,
          files:
            msg.files?.map(file => ({
              name: file.filename,
              url: file.url,
              msgtype: file.msgtype,
              size: file.info?.size || file.size,
              thumbnailUrl: file.thumbnail_url || null,
            })) || [],
        })),
      };

      // Add AI-specific fields if in AI mode
      if (this.mode === 'ai') {
        payload.source = 'ai-chat';
        payload.sessionId = this.sessionId;
      }

      const result = await uploadMessagesForAI(payload);
      if (result?.data?.status && result.data.status !== 'success') {
        throw new Error(
          typeof result.data.response === 'string'
            ? result.data.response
            : 'Messages could not be exported.',
        );
      }
      const tasks = result?.data?.response.tasks;
      const projects = result?.data?.response.projects;
      const projectId = result?.data?.response.projectId;

      // Update the project state after a successful chat export.
      if (this.mode === 'chat') {
        this.#handleProjectsStateUpdate(tasks, projects, projectId);
      }

      // For update actions, refetch the project to reflect new column values in the UI
      if (this.action === 'update-task' || this.action === 'update-project') {
        const refetchProjectId = this.selectedProjectId;
        if (refetchProjectId) {
          // Invalidate cache so getProjectById fetches fresh data
          LoadedFolderIds.setLoadedFolderIds(
            LoadedFolderIds.loadedFolderIds.filter(
              id => id !== refetchProjectId,
            ),
          );
          await ProjectsState.getProjectById(refetchProjectId);
          // Dispatch RESET to force ListView to re-render rows with updated column values.
          // #handleProjectsStateChange skips re-renders when only column values change
          // (same item count, no adds/removes), so we need to trigger #refreshRows explicitly.
          columnUpdate.dispatch({
            type: COLUMN_EVENT_TYPES.RESET,
            currentFolderId: refetchProjectId,
          });
        }
      }

      // Show success state briefly
      this.isLoading = false;
      this.isSuccess = true;
      this.#updateButtonState();

      // Show success message
      const actionLabel = ACTION_LABELS[this.action] || 'Action';
      toast.success(`${actionLabel} completed successfully!`);

      // Wait a moment before navigating to show success state
      setTimeout(() => {
        // Exit selection mode and return to previous view
        if (this.mode === 'ai') {
          aiSelectionModeChanged.dispatch(false);
        } else {
          selectionModeChanged.dispatch(false);
        }

        if (this.returnToThread) {
          this.returnToThread();
        }
      }, 800);
    } catch (error) {
      console.error('Failed to upload messages:', error);
      toast.error(
        error.message || 'Failed to process messages. Please try again.',
      );

      // Reset loading state on error
      this.isLoading = false;
      this.isSuccess = false;
      this.#updateButtonState();
    }
  }

  #handleFormError() {
    switch (this.action) {
      case 'create-project':
        return { status: 'success', message: '' };
      case 'update-project':
        if (!this.selectedProjectId)
          return {
            status: 'error',
            message: 'Please select a project to update.',
          };
        return { status: 'success', message: '' };
      case 'create-task':
        if (!this.selectedProjectId)
          return {
            status: 'error',
            message: 'Please select a project to create a task in.',
          };
        return { status: 'success', message: '' };
      case 'update-task':
        if (!this.selectedProjectId)
          return {
            status: 'error',
            message: 'Please select a project to update a task in.',
          };
        if (!this.selectedTaskId)
          return {
            status: 'error',
            message: 'Please select a task to update.',
          };
        return { status: 'success', message: '' };
      default:
        return { status: 'error', message: 'Unknown action.' };
    }
  }

  async #handleProjectsStateUpdate(tasks, projects, projectId) {
    const currentState = ProjectsState.projects;
    if (tasks && tasks.length > 0) {
      // update the tasks in the projects state - 1st check if the tasks are already in the projects state, then add it with the new task info (name and columns)
      tasks.forEach(task => {
        const columnUpdates = task.columnUpdates;
        const currentTask = currentState.tasks.find(t => t.id === task.id);

        // we will check on each columns the column id and then check if the currentState.tasks[task.id].columnValues[columnId] is different from the columnUpdate.value, if so, update the column value or just add it if it doesn't exist
        if (currentTask) {
          columnUpdates.forEach(columnUpdate => {
            const columnId = columnUpdate.columnId;
            const value = columnUpdate.value;

            // if a column value is different from the columnUpdate.value, update the column value or just add it if it doesn't exist
            if (
              currentTask.columnValues.find(cv => cv.columnId === columnId)
                ?.value !== value
            ) {
              currentTask.columnValues.find(
                cv => cv.columnId === columnId,
              ).value = value;
            }
          });
          ProjectsState.setProjects({
            ...currentState,
            tasks: [...currentState.tasks, currentTask],
          });
        } else {
          const newTask = {
            id: task.id,
            name: task.name,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            thumbnail: null,
            parentId: projectId,
            sharingId: task.sharingId,
            columnValues: task.columnUpdates,
            uid: task.uid,
            organizationId: task.organizationId,
            cadfileAccessLevel: null,
            commentsEnabled: 1,
            isShared: 0,
            reconstructions: task.reconstructionResults,
            rowOrder: null,
          };

          ProjectsState.setProjects({
            ...currentState,
            tasks: [...currentState.tasks, newTask],
          });
        }
      });
    }

    if (projects && projects.length > 0) {
      // update the projects in the projects state - 1st check if the projects are already in the projects state, then add it with the new project info (name and columns)
      projects.forEach(project => {
        const columnUpdates = project.columnUpdates;
        const currentFolder = currentState.folders.find(
          p => p.id === project.id,
        );

        // we will check on each columns the column id and then check if the currentState.projects[project.id].columnValues[columnId] is different from the columnUpdate.value, if so, update the column value or just add it if it doesn't exist
        if (currentFolder) {
          columnUpdates.forEach(columnUpdate => {
            const columnId = columnUpdate.columnId;
            const value = columnUpdate.value;

            // if a column value is different from the columnUpdate.value, update the column value or just add it if it doesn't exist
            if (
              currentFolder.columnValues.find(cv => cv.columnId === columnId)
                ?.value !== value
            ) {
              currentFolder.columnValues.find(
                cv => cv.columnId === columnId,
              ).value = value;
            }
          });
          ProjectsState.setProjects({
            ...currentState,
            folders: [...currentState.folders, currentFolder],
          });
        } else {
          const newFolder = {
            id: project.id,
            name: project.folderName,
            createdAt: project.createdAt,
            updatedAt: project.updatedAt,
            metadata: project.metadata,
            parentId: project.parentFolderId,
            accessType: project.accessType,
            sharingId: project.sharingId,
            shortSharingId: project.shortSharingId,
            columnValues: project.columnUpdates,
            customColumns: project.customColumns,
            uid: project.creatorId,
            organizationId: project.organizationId,
            isPublic: project.isPublic,
            isShared: project.isShared,
            rowOrder: project.rowOrder,
            isDefault: project.isDefault || false,
          };

          ProjectsState.setProjects({
            ...currentState,
            folders: [...currentState.folders, newFolder],
          });
        }
      });
    }
  }

}
